import { useState, useMemo } from "react";
import usePageLoader from '../../hooks/usePageLoader';
import { ShimmerStatCard, ShimmerBox, ShimmerTable } from '../../components/common/Shimmer';
import {
  FiPlus, FiRefreshCw, FiTrash2, FiEdit2, FiX,
  FiCheck, FiSearch, FiZap, FiFileText,
} from "react-icons/fi";
import StatusBadge from "../../components/common/StatusBadge";
import Pagination from "../../components/common/Pagination";
import toast from "react-hot-toast";

const INITIAL_CONTENTS = [
  { id: 1, title: "Company Overview",    content: "NexaGPT is an AI chatbot builder that lets you train on any content source including websites, PDFs, Notion, YouTube, and more. No code required.",                                                                        status: "Trained", createdOn: "6 days ago", trainedOn: "6 days ago" },
  { id: 2, title: "Refund Policy",       content: "We offer a 14-day money-back guarantee on all plans. Refunds are processed within 5–7 business days. Contact support@nexagpt.ai to request a refund.",                                                                   status: "Pending", createdOn: "2 days ago", trainedOn: "—" },
  { id: 3, title: "Pricing Explanation", content: "Starter plan is $29/month with 2 chatbots and 10k messages. Pro is $79/month with 10 chatbots, 100k messages, and CRM integrations. Enterprise is custom pricing.",                                                       status: "Trained", createdOn: "1 day ago",  trainedOn: "1 day ago" },
  { id: 4, title: "Support Guidelines",  content: "Support is available via email at support@nexagpt.ai and live chat during business hours (9am–6pm IST, Mon–Fri). Enterprise plans get a dedicated Slack channel.",                                                        status: "Trained", createdOn: "3 days ago", trainedOn: "3 days ago" },
  { id: 5, title: "Onboarding Steps",    content: "Step 1: Create your account. Step 2: Connect your first content source. Step 3: Customize the chatbot appearance. Step 4: Copy the embed code and paste it on your website. Step 5: Monitor conversations in the Inbox.", status: "Failed",  createdOn: "5 days ago", trainedOn: "—" },
];

const PAGE_SIZE = 6;

function ContentPageSkeleton() {
  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 px-6 py-5 space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <ShimmerStatCard key={i} />)}
      </div>
      <div className="flex items-center gap-3">
        <ShimmerBox h="h-9" w="w-56" rounded="rounded-lg" />
        <div className="flex gap-2">
          {[...Array(4)].map((_, i) => <ShimmerBox key={i} h="h-8" w="w-20" rounded="rounded-lg" />)}
        </div>
      </div>
      <ShimmerTable rows={6} cols={5} />
    </div>
  );
}

export default function CustomContent() {
  const [contents, setContents] = useState(INITIAL_CONTENTS);
  const loading = usePageLoader();
  const [selectedIds, setSelectedIds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContent, setEditContent] = useState("");

  /* ── Derived ── */
  const counts = useMemo(() => ({
    total:   contents.length,
    trained: contents.filter(c => c.status === "Trained").length,
    pending: contents.filter(c => c.status === "Pending").length,
    failed:  contents.filter(c => c.status === "Failed").length,
    chars:   contents.reduce((s, c) => s + c.content.length, 0),
  }), [contents]);

  const filtered = useMemo(() => contents.filter(c => {
    const okFilter = filter === "all" || c.status === filter;
    const okSearch = !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.content.toLowerCase().includes(search.toLowerCase());
    return okFilter && okSearch;
  }), [contents, filter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageIds    = paginated.map(c => c.id);
  const allPageSel = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));

  const changeFilter = (f) => { setFilter(f); setPage(1); setSelectedIds([]); };
  const changeSearch = (s) => { setSearch(s); setPage(1); };
  const toggleSel    = (id) => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAllSel = () => {
    if (allPageSel) setSelectedIds(p => p.filter(id => !pageIds.includes(id)));
    else setSelectedIds(p => [...new Set([...p, ...pageIds])]);
  };

  const simulateRetrain = (ids, cb) => {
    setContents(p => p.map(c => ids.includes(c.id) ? { ...c, status: "Processing" } : c));
    setTimeout(() => {
      setContents(p => p.map(c => ids.includes(c.id) ? { ...c, status: "Trained", trainedOn: "Just now" } : c));
      cb?.();
    }, 2000);
  };

  /* ── Handlers ── */
  const handleAdd = () => {
    if (!title.trim() || !content.trim()) { toast.error("Please fill in both title and content."); return; }
    setContents(p => [{ id: Date.now(), title: title.trim(), content: content.trim(), status: "Pending", createdOn: "Just now", trainedOn: "—" }, ...p]);
    setTitle(""); setContent(""); setShowAdd(false); setPage(1);
    toast.success("Content added and queued for training.");
  };

  const handleDelete = (id) => {
    setContents(p => p.filter(c => c.id !== id));
    setSelectedIds(p => p.filter(x => x !== id));
    toast.success("Content removed.");
  };

  const handleRetrain = (id) => simulateRetrain([id], () => toast.success("Content retrained."));

  const handleBulkDelete = () => {
    const n = selectedIds.length;
    setContents(p => p.filter(c => !selectedIds.includes(c.id)));
    setSelectedIds([]); toast.success(`${n} item${n > 1 ? "s" : ""} deleted.`);
  };

  const handleBulkRetrain = () => {
    const ids = [...selectedIds];
    simulateRetrain(ids, () => { setSelectedIds([]); toast.success(`${ids.length} item${ids.length > 1 ? "s" : ""} retrained.`); });
  };

  const handleTrainAllPending = () => {
    const ids = contents.filter(c => c.status === "Pending").map(c => c.id);
    if (!ids.length) return;
    simulateRetrain(ids, () => toast.success(`${ids.length} pending item${ids.length > 1 ? "s" : ""} trained.`));
  };

  const startEdit = (item) => { setEditingId(item.id); setEditTitle(item.title); setEditContent(item.content); };
  const saveEdit  = (id) => {
    setContents(p => p.map(c => c.id === id ? { ...c, title: editTitle, content: editContent, status: "Pending", trainedOn: "—" } : c));
    setEditingId(null);
    toast.success("Content updated and queued for retraining.");
  };

  const fmtChars = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);

  return (
    <div className="-m-6 flex flex-col" style={{ height: "calc(100vh - 4rem)" }}>

      {/* ── Sticky header ── */}
      <div className="shrink-0 bg-white border-b px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Custom Content</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {counts.total} entries · {counts.trained} trained · {fmtChars(counts.chars)} chars total
          </p>
        </div>
        <div className="flex items-center gap-2">
          {counts.pending > 0 && (
            <button onClick={handleTrainAllPending} className="flex items-center gap-2 text-sm border border-primary text-primary px-4 py-2 rounded-lg hover:bg-primary/5 transition">
              <FiZap size={14} /> Train {counts.pending} Pending
            </button>
          )}
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm hover:opacity-90 transition"
          >
            {showAdd ? <FiX size={14} /> : <FiPlus size={14} />}
            {showAdd ? "Cancel" : "Add Content"}
          </button>
        </div>
      </div>

      {loading ? <ContentPageSkeleton /> : <div className="flex-1 overflow-y-auto bg-gray-50">

        {/* Add content panel */}
        {showAdd && (
          <div className="bg-white border-b shadow-sm">
            <div className="px-6 py-5 space-y-3 max-w-2xl">
              <p className="text-sm text-gray-500">Paste any text — FAQs, policies, product info — to train your chatbot.</p>
              <input
                type="text" value={title} onChange={e => setTitle(e.target.value)}
                placeholder="Title (e.g. Refund Policy)"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                autoFocus
              />
              <textarea
                rows={5} value={content} onChange={e => setContent(e.target.value)}
                placeholder="Paste your content here. The more detail you provide, the better your chatbot will answer questions about this topic."
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">{content.length} characters</span>
                <button onClick={handleAdd} className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm hover:opacity-90">
                  <FiPlus size={14} /> Add Content
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-6 space-y-4">

          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard label="Total Entries" value={counts.total}               color="text-gray-900" />
            <KpiCard label="Trained"       value={counts.trained}             color="text-green-600" dot="bg-green-500" />
            <KpiCard label="Pending / Failed" value={`${counts.pending} / ${counts.failed}`} color="text-yellow-600" dot="bg-yellow-500" />
            <KpiCard label="Total Chars"   value={fmtChars(counts.chars)}     color="text-primary" />
          </div>

          {/* Search + filter */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text" value={search} onChange={e => changeSearch(e.target.value)}
                placeholder="Search content…"
                className="w-full pl-9 pr-8 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
              />
              {search && <button onClick={() => changeSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX size={13} /></button>}
            </div>
            <div className="flex items-center border rounded-lg overflow-hidden text-xs font-medium shrink-0">
              {["all", "Trained", "Pending", "Failed"].map(f => (
                <button key={f} onClick={() => changeFilter(f)} className={`px-3 py-1.5 transition ${filter === f ? "bg-primary text-white" : "text-gray-500 hover:bg-gray-50"}`}>
                  {f === "all" ? "All" : f}
                </button>
              ))}
            </div>
          </div>

          {/* Bulk bar */}
          {selectedIds.length > 0 && (
            <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-xl px-4 py-2.5">
              <span className="text-sm font-medium text-primary">{selectedIds.length} selected</span>
              <div className="flex gap-2 ml-auto">
                <button onClick={handleBulkRetrain} className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50">
                  <FiRefreshCw size={12} /> Retrain Selected
                </button>
                <button onClick={handleBulkDelete} className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
                  <FiTrash2 size={12} /> Delete Selected
                </button>
                <button onClick={() => setSelectedIds([])} className="text-gray-400 hover:text-gray-600 p-1"><FiX size={14} /></button>
              </div>
            </div>
          )}

          {/* Content list */}
          <div className="bg-white border rounded-xl overflow-hidden">
            {/* Table header */}
            <div className="bg-gray-50 border-b px-4 py-3 flex items-center gap-3">
              <input type="checkbox" checked={allPageSel} onChange={toggleAllSel} className="accent-primary shrink-0" />
              <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Content Entry</span>
              <span className="text-xs text-gray-500 uppercase tracking-wide font-semibold ml-auto">Actions</span>
            </div>

            {paginated.length === 0 ? (
              <div className="text-center py-16">
                <FiFileText size={30} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm text-gray-500">
                  {search || filter !== "all" ? "No content matches your filters." : "No content added yet."}
                </p>
                {!search && filter === "all" && (
                  <button onClick={() => setShowAdd(true)} className="mt-3 text-sm text-primary hover:underline">
                    Add your first content entry
                  </button>
                )}
              </div>
            ) : (
              <div className="divide-y">
                {paginated.map(item => (
                  <div key={item.id} className={`px-4 py-4 hover:bg-gray-50 transition ${selectedIds.includes(item.id) ? "bg-primary/5" : ""}`}>
                    {editingId === item.id ? (
                      <div className="space-y-2">
                        <input
                          value={editTitle} onChange={e => setEditTitle(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                        />
                        <textarea
                          rows={4} value={editContent} onChange={e => setEditContent(e.target.value)}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400">{editContent.length} characters</span>
                          <div className="flex gap-2">
                            <button onClick={() => saveEdit(item.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:opacity-90">
                              <FiCheck size={12} /> Save
                            </button>
                            <button onClick={() => setEditingId(null)} className="flex items-center gap-1 px-3 py-1.5 text-xs border rounded-lg hover:bg-gray-50">
                              <FiX size={12} /> Cancel
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <input
                            type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSel(item.id)}
                            className="accent-primary mt-0.5 shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <p className="font-medium text-gray-900 text-sm">{item.title}</p>
                              <StatusBadge status={item.status} />
                            </div>
                            <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{item.content}</p>
                            <p className="text-xs text-gray-400 mt-1.5">
                              {item.content.length} chars · Added {item.createdOn} · Trained {item.trainedOn}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {item.status === "Processing" ? (
                            <span className="flex items-center gap-1.5 text-xs text-blue-600 px-3 py-1.5">
                              <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              Training…
                            </span>
                          ) : (
                            <>
                              <button onClick={() => startEdit(item)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition">
                                <FiEdit2 size={12} /> Edit
                              </button>
                              <button onClick={() => handleRetrain(item.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition">
                                <FiRefreshCw size={12} /> Retrain
                              </button>
                              <button onClick={() => handleDelete(item.id)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition">
                                <FiTrash2 size={12} /> Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination footer */}
          {filtered.length > PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-gray-500">
              <p>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} entries</p>
              <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            </div>
          )}

        </div>
      </div>}
    </div>
  );
}

function KpiCard({ label, value, color, dot }) {
  return (
    <div className="bg-white border rounded-xl px-4 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        {dot && <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />}
        <p className="text-xs text-gray-500 font-medium truncate">{label}</p>
      </div>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}
